<template>
  <Modal
    class="available-balance-modal"
    data-testid="available-balance-modal"
    :is-open="isOpen"
    size="md"
    title="Saldo disponível"
    @close="handleClose"
  >
    <HeadingDisplay
      class="available-balance-modal__header"
      title="Disponível para negociar na Bolsa"
      subtitle="Este saldo pode ser maior que o disponível na conta, pois considera valores a receber da venda de ativos."
      size="small"
    />
    <section v-if="error">
      <HelperViewStatus
        generic-error
        button-label="Tentar novamente"
        @click="handleRetry"
      />
    </section>
    <ContentDisplayLoader
      v-else-if="loading"
      data-testid="available-balance-modal-loader"
      class="available-balance-modal__content"
    >
      <ListDisplayLoader class="available-balance-modal__content__value" />
      <ListDisplayLoader class="available-balance-modal__content__value" />
      <ListDisplayLoader class="available-balance-modal__content__value" />
    </ContentDisplayLoader>
    <ContentDisplay
      v-else
      class="available-balance-modal__content"
      :blocks-length="1"
    >
      <template #content-block-1>
        <ListDisplay
          class="available-balance-modal__content__value"
          title="Saldo em conta"
          :value="availableOnAccountValueFormatted"
        />
        <ListDisplay
          class="available-balance-modal__content__value"
          title="Saldo a liquidar"
          :value="availableToSettleValue"
        />
        <ListDisplay
          class="available-balance-modal__content__value available-balance-modal__content__value--bold"
          title="Total disponível para negociar em Bolsa"
          :value="availableToOperateValue"
          value-as-bold
        />
      </template>
    </ContentDisplay>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import {
  Modal,
  HeadingDisplay,
  HelperViewStatus,
  ContentDisplay,
  ListDisplay
} from '@warrenbrasil/nebraska-web';
import { ContentDisplayLoader } from '@/modules/trade/components/ContentDisplayLoader';
import { ListDisplayLoader } from '@/modules/trade/components/ListDisplayLoader';

import { AvailableBalanceData } from '@/modules/trade/services/unified-account/types';

interface Props {
  isOpen: boolean;
  error: boolean;
  loading: boolean;
  availableBalanceData: AvailableBalanceData | null;
}
const props = defineProps<Props>();

interface Emits {
  'on-close': () => void;
  'on-retry': () => void;
}
const emit = defineEmits<Emits>();

const availableOnAccountValueFormatted = computed(() => {
  return props.availableBalanceData?.availableOnAccountValue?.formatted || '-';
});

const availableToSettleValue = computed(() => {
  return props.availableBalanceData?.toSettleValue?.formatted || '-';
});

const availableToOperateValue = computed(() => {
  return props.availableBalanceData?.availableToOperateValue?.formatted || '-';
});

const handleClose = () => {
  emit('on-close');
};

const handleRetry = () => {
  emit('on-retry');
};
</script>
<style lang="less" scoped>
::v-deep .modal-content {
  padding: @size-spacing-x600 @size-spacing-x800 @size-spacing-x1000
    @size-spacing-x800;
  text-align: left;
}

.text-bold {
  font-weight: bold;
}

.available-balance-modal {
  &__header {
    margin-bottom: @size-spacing-x600;
  }

  &__content {
    width: 100%;

    &__loading {
      margin: @size-spacing-x300 @size-spacing-x400 0 @size-spacing-x400;
    }

    &__loading:last-child {
      margin: @size-spacing-x300 @size-spacing-x400 @size-spacing-x500
        @size-spacing-x400;
    }

    &__value {
      margin-bottom: @size-spacing-x350;

      &--bold {
        ::v-deep .list-display__slot-title p {
          color: @element-primary !important;
        }
      }

      &:last-child {
        margin: 0;
      }
    }
  }
}
</style>
